/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import OpenAI from 'openai'
import pptxgen from 'pptxgenjs'
import { useEffect, useState } from 'react'
import './App.css'
import logo from './logo.png'
import AccessDenied from './components/AccessDenied'
import { languages, defaultLanguage } from './config/languages'
// test

// Move this outside the component to avoid recreating on each render
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
})

// At the top of the file, after imports
const debugEnvironment = () => {
  console.group('🔍 Environment Debug Information');
  
  // Telegram WebApp object
  console.group('📱 Telegram WebApp');
  console.log('window.Telegram exists:', !!window.Telegram);
  console.log('window.Telegram?.WebApp exists:', !!window.Telegram?.WebApp);
  if (window.Telegram?.WebApp) {
    const tg = window.Telegram.WebApp;
    console.log('WebApp Platform:', tg.platform);
    console.log('WebApp Version:', tg.version);
    console.log('WebApp Init Data:', tg.initData);
    console.log('WebApp Init Data Unsafe:', tg.initDataUnsafe);
    console.log('WebApp Color Scheme:', tg.colorScheme);
    console.log('WebApp Theme Params:', tg.themeParams);
  }
  console.groupEnd();

  // Browser Information
  console.group('🌐 Browser Environment');
  console.log('User Agent:', navigator.userAgent);
  console.log('Platform:', navigator.platform);
  console.log('Vendor:', navigator.vendor);
  console.log('Language:', navigator.language);
  console.log('Online Status:', navigator.onLine);
  console.log('Cookies Enabled:', navigator.cookieEnabled);
  console.log('Document Referrer:', document.referrer);
  console.log('Window Inner Size:', {
    width: window.innerWidth,
    height: window.innerHeight
  });
  console.groupEnd();

  // Screen Information
  console.group('📺 Screen Information');
  console.log('Screen Size:', {
    width: window.screen.width,
    height: window.screen.height
  });
  console.log('Screen Available Size:', {
    width: window.screen.availWidth,
    height: window.screen.availHeight
  });
  console.log('Device Pixel Ratio:', window.devicePixelRatio);
  console.groupEnd();

  console.groupEnd();
};

function App() {
  const [formData, setFormData] = useState({
    topic: '',
    numSlides: 10,
    style: 'general',
    userName: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [generatedPPTX, setGeneratedPPTX] = useState(null)
  const [presentationTopic, setPresentationTopic] = useState('')
  const [telegramUser, setTelegramUser] = useState(null)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [successMessage, setSuccessMessage] = useState('');
  const [language, setLanguage] = useState(defaultLanguage);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    // Run debug logging immediately
    debugEnvironment();

    const checkAccess = () => {
      const tg = window.Telegram?.WebApp;
      const isDevelopment = process.env.NODE_ENV === 'development';
      const isTelegramPlatform = tg?.platform && tg.platform !== 'unknown';
      
      console.log('🔐 Authorization Check:', {
        environment: process.env.NODE_ENV,
        hasTelegramObject: !!window.Telegram,
        hasWebAppObject: !!tg,
        platform: tg?.platform || 'none',
        initDataUnsafe: tg?.initDataUnsafe || {},
        isTelegramPlatform,
        isDevelopment,
        timestamp: new Date().toISOString()
      });

      // Allow access in development mode
      setIsAuthorized(isDevelopment || isTelegramPlatform);
      setIsCheckingAuth(false);

      if (isTelegramPlatform) {
        console.log('✅ Telegram WebApp initialized on platform:', tg.platform);
        tg.ready();
        tg.expand();

        // Set theme colors
        const themeParams = tg.themeParams || {};
        console.log('🎨 Setting theme parameters:', themeParams);
        
        document.documentElement.style.setProperty(
          '--tg-theme-bg-color',
          themeParams.bg_color || '#ffffff'
        );
        document.documentElement.style.setProperty(
          '--tg-theme-text-color',
          themeParams.text_color || '#000000'
        );
        document.documentElement.style.setProperty(
          '--tg-theme-button-color',
          themeParams.button_color || '#3390ec'
        );
        document.documentElement.style.setProperty(
          '--tg-theme-button-text-color',
          themeParams.button_text_color || '#ffffff'
        );
      } else if (isDevelopment) {
        console.log('🔧 Development mode: bypassing Telegram WebApp check');
      } else {
        console.log('❌ Not running in valid Telegram WebApp context');
        if (tg?.platform === 'unknown') {
          console.warn('⚠️ Detected possible browser access attempt');
        }
      }
    };

    checkAccess();
  }, [])

  if (isCheckingAuth) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (!isAuthorized) {
    return (
      <>
        <AccessDenied />
        <div className="position-fixed bottom-0 end-0 m-3">
          <button 
            className="btn btn-sm btn-secondary"
            onClick={debugEnvironment}
          >
            Debug Environment
          </button>
        </div>
      </>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name === 'numSlides') {
      const numValue = value === '' ? '' : parseInt(value)
      setFormData((prev) => ({
        ...prev,
        [name]: numValue
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const generateContent = async () => {
    try {
      console.log('API Key loaded:', !!process.env.REACT_APP_OPENAI_API_KEY)

      const response = await openai.chat.completions.create({
        model: 'gpt-4-0125-preview',
        messages: [
          {
            role: 'system',
            content: `You are a presentation expert. Generate presentation content in ${languages[language].name.split(' ')[1]} language in JSON format with title and content for each slide.`
          },
          {
            role: 'user',
            content: `Create a ${formData.numSlides}-slide presentation about ${formData.topic} in ${formData.style} style. 
              Format the response as a JSON object with a 'presentation' array containing objects with 'title' and 'content' properties.
              Keep each slide's content under 700 characters.
              Make it engaging and professional.
              All content must be in ${languages[language].name.split(' ')[1]} language.
              Structure the response exactly like this example:
              {
                "presentation": [
                  {"title": "Introduction to Topic", "content": "Main points of slide..."},
                  {"title": "Key Aspects", "content": "Details about..."}
                ]
              }`
          }
        ],
        temperature: 0.7,
        max_tokens: 2000,
        response_format: { type: 'json_object' }
      });

      // Parse the JSON response
      const parsedResponse = JSON.parse(response.choices[0].message.content)
      let content = parsedResponse.presentation

      // Calculate how many content slides we need
      // Total = content slides + title slide + thank you slide + (agenda slide if > 5)
      const needsAgenda = formData.numSlides > 5
      const contentSlidesNeeded = formData.numSlides - (needsAgenda ? 3 : 2)

      // Trim or pad the content slides as needed
      if (content.length > contentSlidesNeeded) {
        content = [
          content[0], // Keep title
          ...content.slice(1, contentSlidesNeeded + 1), // Keep exact number of content slides
          content[content.length - 1] // Keep thank you
        ]
      }

      return content
    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        type: error.type,
        code: error.code
      })
      throw error
    }
  }

  const handleDownload = async () => {
    if (generatedPPTX) {
      try {
        setIsDownloading(true);
        
        // Generate blob from PPTX
        const blob = await generatedPPTX.write('blob');
        const reader = new FileReader();
        
        reader.readAsDataURL(blob);
        reader.onloadend = async function () {
          const base64data = reader.result.split(',')[1];

          try {
            // Simple request to save the PPTX
            const response = await fetch(
              `${process.env.REACT_APP_RAILWAY_STATIC_URL}/save-pptx`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Origin': window.location.origin
                },
                body: JSON.stringify({
                  pptx: base64data,
                  name: `${presentationTopic || 'presentation'}.pptx`
                })
              }
            );

            if (!response.ok) {
              const errorData = await response.json();
              console.error('API Error Response:', errorData);
              throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Save result:', result);
            
            if (result.error) {
              throw new Error(result.error);
            }

            // If successful, send the ID back to Telegram
            if (result.id && window.Telegram?.WebApp) {
              // Send data to Telegram - this will trigger the WebApp to close
              window.Telegram.WebApp.sendData(JSON.stringify({ id: result.id }));
              
              // Don't reset states here as the WebApp will close anyway
              // Keep the loading state active until the WebApp closes
            }
          } catch (error) {
            // Only reset loading state if there's an error
            setIsDownloading(false);
            console.error('Error saving presentation:', error);
            
            if (window.Telegram?.WebApp) {
              window.Telegram.WebApp.showPopup({
                message: `${languages[language].translations.errorMessages.general}`,
                buttons: [{type: 'close'}]
              });
            } else {
              alert(error.message);
            }
          }
        };
      } catch (error) {
        // Reset loading state for blob generation errors
        setIsDownloading(false);
        console.error('Error generating blob:', error);
        
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showPopup({
            message: `${languages[language].translations.errorMessages.general}`,
            buttons: [{type: 'close'}]
          });
        } else {
          alert(error.message);
        }
      }
    }
  };

  const generatePresentation = async () => {
    const slideCount = parseInt(formData.numSlides)
    if (isNaN(slideCount) || slideCount < 3 || slideCount > 12) {
      alert(languages[language].translations.errorMessages.slides)
      return
    }

    setIsLoading(true)
    setPresentationTopic(formData.topic)
    setSuccessMessage('');

    try {
      const content = await generateContent()

      if (!Array.isArray(content) || content.length === 0) {
        throw new Error('Invalid content format received from AI')
      }

      let pptx = new pptxgen()

      const themes = {
        general: {
          background: 'FFFFFF',
          titleColor: '2D3436', // Modern dark gray
          textColor: '636E72',
          accentColor: '00B894', // Mint
          secondaryAccentColor: 'F1F2F6', // Light gray
          tertiaryAccentColor: 'DFE6E9' // Subtle blue-gray
        },
        business: {
          background: 'FFFFFF',
          titleColor: '2C3E50', // Dark blue
          textColor: '34495E',
          accentColor: '3498DB', // Corporate blue
          secondaryAccentColor: 'ECF0F1', // Light gray
          tertiaryAccentColor: 'BDC3C7' // Medium gray
        },
        education: {
          background: 'FFFFFF',
          titleColor: '6C5CE7', // Soft purple
          textColor: '484B6A',
          accentColor: 'A8E6CF', // Mint green
          secondaryAccentColor: 'F3F3F3', // Light gray
          tertiaryAccentColor: 'FFD3B6' // Soft orange
        },
        tech: {
          background: '1A1A2E', // Dark navy
          titleColor: 'FFFFFF',
          textColor: 'E9E9E9',
          accentColor: '0F3460', // Deep blue
          secondaryAccentColor: '16213E', // Midnight blue
          tertiaryAccentColor: '533483' // Purple
        }
      }

      const theme = themes[formData.style] || themes.general

      pptx.layout = 'LAYOUT_16x9'
      pptx.author = formData.userName
      pptx.title = presentationTopic

      // Title Slide (First Slide)
      let titleSlide = pptx.addSlide()
      titleSlide.background = { color: theme.background }

      // Add title
      titleSlide.addText(formData.topic, {
        x: '10%',
        y: '35%',
        w: '80%',
        h: 1.5,
        fontSize: 44,
        color: theme.titleColor,
        bold: true,
        align: 'center',
        fontFace: 'Arial'
      })

      // Add user name and date
      const currentDate = new Date().toLocaleDateString()
      titleSlide.addText(
        [
          { text: formData.userName, options: { fontSize: 20, bold: true } },
          { text: '\n' + currentDate, options: { fontSize: 16 } }
        ],
        {
          x: '10%',
          y: '80%',
          w: '80%',
          h: 1,
          color: theme.titleColor,
          align: 'center'
        }
      )

      // After the title slide and before the content slides loop, add agenda slide if needed
      if (content.length > 5) {
        let agendaSlide = pptx.addSlide()
        agendaSlide.background = { color: theme.background }

        // Modern agenda layout
        agendaSlide.addShape(pptx.ShapeType.rect, {
          x: 0,
          y: 0,
          w: '30%',
          h: '100%',
          fill: { color: theme.accentColor },
          opacity: 0.1
        })

        agendaSlide.addText('Agenda', {
          x: '5%',
          y: '10%',
          w: '90%',
          h: 1,
          fontSize: 36,
          color: theme.titleColor,
          bold: true,
          align: 'left'
        })

        // Create agenda items from slide titles
        const agendaItems = content.slice(1, -1).map((slide, i) => slide.title)

        agendaSlide.addText(agendaItems.join('\n'), {
          x: '35%',
          y: '10%',
          w: '60%',
          h: 4,
          fontSize: 18,
          color: theme.textColor,
          align: 'left',
          lineSpacing: 32,
          bullet: { type: 'number' }
        })
      }

      // Update the content slides loop with 6 modern layouts
      const needsAgenda = formData.numSlides > 5

      content.forEach((slide, idx) => {
        // Skip first and last slides
        if (idx === 0 || idx === content.length - 1) return

        // Calculate actual slide number for display
        const slideNumber = needsAgenda ? idx : idx + 1

        let pptSlide = pptx.addSlide()
        pptSlide.background = { color: theme.background }

        // Professional layouts rotation
        switch (idx % 6) {
          case 0:
            // Modern Split with Geometric Accent
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: 0,
              w: '40%',
              h: '100%',
              fill: { color: theme.accentColor }
            })

            // Decorative elements (replacing circles with small rectangles)
            ;[0.2, 0.4, 0.6].forEach((pos) => {
              pptSlide.addShape(pptx.ShapeType.rect, {
                x: '35%',
                y: `${pos * 100 - 1}%`,
                w: '2%',
                h: '2%',
                fill: { color: theme.tertiaryAccentColor }
              })
            })

            pptSlide.addText(slide.title, {
              x: '5%',
              y: '40%',
              w: '30%',
              h: 1,
              fontSize: 28,
              color: 'FFFFFF',
              bold: true,
              align: 'left'
            })

            pptSlide.addText(slide.content, {
              x: '45%',
              y: '20%',
              w: '50%',
              h: 4,
              fontSize: 18,
              color: theme.textColor,
              align: 'left',
              lineSpacing: 32
            })
            break

          case 1:
            // Modern Minimalist Layout
            // Background accent
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: 0,
              w: '100%',
              h: '100%',
              fill: { color: theme.secondaryAccentColor },
              opacity: 0.05
            })

            // Left accent bar
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '5%',
              y: '10%',
              w: '2%',
              h: '80%',
              fill: { color: theme.accentColor },
              opacity: 0.3
            })

            // Title container
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '5%',
              y: '5%',
              w: '90%',
              h: '15%',
              fill: { color: theme.accentColor }
            })

            pptSlide.addText(slide.title, {
              x: '5%',
              y: '5%',
              w: '90%',
              h: '15%',
              fontSize: 28,
              color: 'FFFFFF',
              bold: true,
              align: 'left',
              valign: 'middle',
              margin: 10,
              fit: 'shrink',
              wrap: false
            })

            // Content container - Moved up to reduce gap
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '10%',
              y: '22%', // Reduced from 35% to 22%
              w: '80%',
              h: '73%', // Increased height to compensate
              fill: { color: theme.secondaryAccentColor },
              opacity: 0.1
            })

            // Content text - Adjusted to match new container position
            pptSlide.addText(slide.content, {
              x: '12%',
              y: '24%', // Reduced from 38% to 24%
              w: '76%',
              h: '69%', // Increased height
              fontSize: 18,
              color: theme.textColor,
              align: 'left',
              lineSpacing: 32,
              valign: 'top', // Ensure text starts from top
              breakLine: true,
              fit: 'shrink'
            })
            break

          case 2:
            // Modern Diagonal Layout
            // Background accent rectangle
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: 0,
              w: '100%',
              h: '100%',
              fill: { color: theme.accentColor },
              opacity: 0.1
            })

            // Diagonal accent elements
            for (let i = 0; i < 3; i++) {
              pptSlide.addShape(pptx.ShapeType.rect, {
                x: `${i * 25}%`,
                y: '5%',
                w: '20%',
                h: '90%',
                fill: { color: theme.accentColor },
                rotate: 15,
                opacity: 0.07
              })
            }

            // Content container
            pptSlide.addShape(pptx.ShapeType.roundRect, {
              x: '45%',
              y: '20%',
              w: '50%',
              h: '70%',
              fill: { color: theme.background },
              shadow: { type: 'outer', blur: 3, offset: 2, angle: 45 },
              radius: 5,
              line: { color: theme.accentColor, width: 0.5, opacity: 0.5 }
            })

            // Title area with accent bar
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '5%',
              y: '20%',
              w: '35%',
              h: '15%',
              fill: { color: theme.accentColor }
            })

            pptSlide.addText(slide.title, {
              x: '7%',
              y: '22%',
              w: '31%',
              h: 1,
              fontSize: 28,
              color: 'FFFFFF',
              bold: true,
              align: 'left'
            })

            pptSlide.addText(slide.content, {
              x: '48%',
              y: '25%',
              w: '44%',
              h: 4,
              fontSize: 16,
              color: theme.textColor,
              align: 'left',
              lineSpacing: 28,
              breakLine: true,
              valign: 'top',
              margin: 0,
              inset: 0,
              fit: 'shrink'
            })
            break

          case 3:
            // Modern Cards Layout
            // Background subtle pattern
            for (let i = 0; i < 10; i++) {
              pptSlide.addShape(pptx.ShapeType.rect, {
                x: `${i * 10}%`,
                y: 0,
                w: '8%',
                h: '100%',
                fill: { color: theme.secondaryAccentColor },
                opacity: 0.1
              })
            }

            // Main content card
            pptSlide.addShape(pptx.ShapeType.roundRect, {
              x: '5%',
              y: '5%',
              w: '90%',
              h: '15%',
              fill: { color: theme.accentColor },
              radius: 5
            })

            pptSlide.addText(slide.title, {
              x: '7%',
              y: '5%',
              w: '86%',
              h: '15%',
              fontSize: 28,
              color: 'FFFFFF',
              bold: true,
              align: 'left',
              valign: 'middle',
              wrap: true,
              fit: 'shrink'
            })

            // Main content - moved down to avoid overlap
            pptSlide.addShape(pptx.ShapeType.roundRect, {
              x: '5%',
              y: '25%',
              w: '90%',
              h: '70%',
              fill: { color: 'FFFFFF' },
              shadow: { type: 'outer', blur: 3, offset: 2, angle: 45 },
              radius: 5
            })

            pptSlide.addText(slide.content, {
              x: '8%',
              y: '28%',
              w: '84%',
              h: '64%',
              fontSize: 16,
              color: '333333',
              align: 'left',
              lineSpacing: 28,
              valign: 'top',
              breakLine: true,
              fit: 'shrink'
            })
            break

          case 4:
            // Modern Spotlight Layout (replacing Circular Focus Layout)
            // Background gradient effect
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '50%',
              y: 0,
              w: '50%',
              h: '100%',
              fill: { color: theme.secondaryAccentColor },
              opacity: 0.1
            })

            // Accent rectangles
            ;[0.2, 0.4, 0.6].forEach((pos) => {
              pptSlide.addShape(pptx.ShapeType.rect, {
                x: `${pos * 100}%`,
                y: '85%',
                w: '15%',
                h: '3%',
                fill: { color: theme.accentColor },
                opacity: 0.2
              })
            })

            // Title container - adjusted height for better vertical centering
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '5%',
              y: '15%',
              w: '90%', // Increased from 50% to 90%
              h: '15%', // Increased from 12% to 15%
              fill: { color: theme.accentColor },
              opacity: 0.1
            })

            // Title text - improved vertical alignment
            pptSlide.addText(slide.title, {
              x: '7%', // Adjusted margin
              y: '15%',
              w: '86%', // Matched with container width
              h: '15%', // Match container height
              fontSize: 32,
              color: theme.titleColor,
              bold: true,
              align: 'left',
              valign: 'middle', // Added vertical alignment
              margin: 10,
              fit: 'shrink' // Added text fitting
            })

            // Decorative accent bar
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '5%',
              y: '32%',
              w: '10%',
              h: '1%',
              fill: { color: theme.accentColor }
            })

            // Content container - expanded width and adjusted position
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '5%',
              y: '35%',
              w: '90%', // Increased from 55% to 90%
              h: '60%', // Increased from 45% to 60%
              fill: { color: theme.secondaryAccentColor },
              opacity: 0.05
            })

            // Content text - adjusted to match container
            pptSlide.addText(slide.content, {
              x: '7%',
              y: '37%',
              w: '86%', // Increased width to match container
              h: '56%', // Match container height
              fontSize: 18,
              color: theme.textColor,
              align: 'left',
              lineSpacing: 32,
              valign: 'top',
              breakLine: true,
              fit: 'shrink' // Added text fitting
            })
            break

          case 5:
            // Modern Frame Layout
            // Top border
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: 0,
              w: '100%',
              h: '5%',
              fill: { color: theme.accentColor },
              opacity: 0.1
            })

            // Bottom border
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: '95%',
              w: '100%',
              h: '5%',
              fill: { color: theme.accentColor },
              opacity: 0.1
            })

            // Left border
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: 0,
              y: 0,
              w: '5%',
              h: '100%',
              fill: { color: theme.accentColor },
              opacity: 0.1
            })

            // Right border
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '95%',
              y: 0,
              w: '5%',
              h: '100%',
              fill: { color: theme.accentColor },
              opacity: 0.1
            })

            // Title container with gradient effect
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '7%',
              y: '10%',
              w: '86%',
              h: '25%',
              fill: { color: theme.secondaryAccentColor }
            })

            // Decorative accent line
            pptSlide.addShape(pptx.ShapeType.rect, {
              x: '7%',
              y: '32%',
              w: '15%',
              h: '1%',
              fill: { color: theme.accentColor }
            })

            pptSlide.addText(slide.title, {
              x: '10%',
              y: '15%',
              w: '80%',
              h: 1.5,
              fontSize: 28,
              color: theme.titleColor,
              bold: true,
              align: 'center',
              breakLine: true
            })

            pptSlide.addText(slide.content, {
              x: '10%',
              y: '35%',
              w: '80%',
              h: 3,
              fontSize: 18,
              color: theme.textColor,
              align: 'left',
              lineSpacing: 32
            })
            break
          default:
            // Handle unexpected cases
            console.warn(`Unexpected slide index: ${idx}`)
            break
        }

        // Enhanced slide number with rectangular background instead of circle
        pptSlide.addShape(pptx.ShapeType.rect, {
          x: '90%',
          y: '90%',
          w: '6%',
          h: '6%',
          fill: { color: theme.accentColor },
          opacity: 0.1,
          radius: 3
        })

        pptSlide.addText(`${slideNumber}`, {
          x: '90%',
          y: '90%',
          w: '6%',
          h: 0.4,
          fontSize: 14,
          color: theme.accentColor,
          bold: true,
          align: 'center'
        })
      })

      // Thank You Slide (Last Slide)
      let thankYouSlide = pptx.addSlide()
      thankYouSlide.background = { color: theme.background }

      // Add "Thank You" text
      thankYouSlide.addText('Thank You', {
        x: '10%',
        y: '40%',
        w: '80%',
        h: 1,
        fontSize: 60,
        color: theme.titleColor,
        bold: true,
        align: 'center'
      })

      setGeneratedPPTX(pptx)
      setFormData({
        topic: '',
        numSlides: 3,
        style: 'general',
        userName: formData.userName
      })
      setSuccessMessage('Successfully generated!');
    } catch (error) {
      console.error('Error:', error)
      let errorMessage = 'Error generating presentation. '

      if (error.message.includes('API key')) {
        errorMessage += 'API key configuration issue.'
      } else if (error.message.includes('Invalid content format')) {
        errorMessage += 'Invalid response from AI service.'
      } else if (error.code === 'insufficient_quota') {
        errorMessage += 'API quota exceeded.'
      } else {
        errorMessage += 'Please try again.'
      }

      alert(errorMessage)
      setPresentationTopic('')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='container'>
      <main>
        <div className='card p-4 pt-0'>
          <div className='mb-3'>
            <label className='form-label'>{languages[language].translations.language}</label>
            <select
              className='form-select'
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {Object.entries(languages).map(([code, lang]) => (
                <option key={code} value={code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className='mb-3'>
            <label className='form-label'>{languages[language].translations.yourName}</label>
            <input
              type='text'
              className='form-control'
              name='userName'
              value={formData.userName}
              onChange={handleInputChange}
              placeholder={languages[language].translations.namePlaceholder}
            />
          </div>

          <div className='mb-3'>
            <label className='form-label'>{languages[language].translations.topic}</label>
            <input
              type='text'
              className='form-control'
              name='topic'
              value={formData.topic}
              onChange={handleInputChange}
              placeholder={languages[language].translations.topicPlaceholder}
            />
          </div>

          <div className='mb-3'>
            <label className='form-label'>{languages[language].translations.numSlides}</label>
            <input
              type='number'
              className='form-control'
              name='numSlides'
              value={formData.numSlides}
              onChange={handleInputChange}
              min='3'
              max='12'
            />
            <small className='text-muted'>{languages[language].translations.slidesHelp}</small>
          </div>

          <div className='mb-4'>
            <label className='form-label'>{languages[language].translations.style}</label>
            <select
              className='form-select'
              name='style'
              value={formData.style}
              onChange={handleInputChange}
            >
              {Object.entries(languages[language].translations.styles).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className='btn-container'>
            {!generatedPPTX ? (
              <button
                className='btn btn-primary btn-lg w-100'
                onClick={generatePresentation}
                disabled={isLoading || !formData.topic}
              >
                {isLoading ? (
                  <span>
                    <span
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    {languages[language].translations.generating}
                  </span>
                ) : (
                  languages[language].translations.generate
                )}
              </button>
            ) : (
              <>
                {successMessage && (
                  <div className='alert alert-success py-2 mb-3'>
                    {languages[language].translations.successMessage}
                  </div>
                )}
                <button
                  className='btn btn-success btn-lg w-100'
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <span>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      {languages[language].translations.downloading}
                    </span>
                  ) : (
                    languages[language].translations.download
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default App
